import {
  IonBadge,
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  RefresherEventDetail,
  useIonViewDidEnter,
} from "@ionic/react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import { appsSharp } from "ionicons/icons";
import WorkOrderActionBar from "../components/WorkOrderActionBar";
import ShareWorkOrderButton from "../components/ShareWorkOrderButton";
import "./WorkOrderOverview.css";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../util/AppInsights";
import PageHeader from "../components/PageHeader";
import { TranslationsContext } from "../util/Translations";
import { getLinkedDocInfoList } from "../api/WorkOrders";
import ApiError from "../components/ApiError";
import WOContent from "../components/WOContent";
import { DEFAULT_PERMISSIONS, useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import useGetWorkOrder from "../hooks/useGetWorkOrder";
import useLoadCompWizardSections from "../hooks/useLoadCompWizardSections";
import { useOfflineStore } from "../store/offline-mode";
import useGetMeterReading from "../hooks/work-order/useGetMeterReading";
import { useNetworkStatusStore } from "../store/network-status";
import debounce from "lodash/debounce";

const WorkOrderOverview: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { workOrderId } = useParams<{ workOrderId: string }>();
  let displayWo = decodeParam(workOrderId);
  const isUnmounted = useRef(false);
  const [showLoading, setShowLoading] = useState(false);
  const [refreshState, setRefreshState] = useState(false);
  const [actionSheetVisible, setActionSheetVisible] = useState(false);
  const [errorData, setErrorData] = useState("");
  const [altBackUrl, setAltBackUrl] = useState<string>();
  const { trigger } = useGetWorkOrder();
  const { loadCompWizardSections } = useLoadCompWizardSections();
  const { getMeterReading } = useGetMeterReading();
  const [isLoading, setIsLoading] = useState(false);

  // work order store
  const workOrder = useWorkOrderStore((state) =>
    state.getWorkOrderById(displayWo)
  );
  const setWoDocuments = useWorkOrderStore((state) => state.setWoDocuments);
  const documents = useWorkOrderStore(
    useShallow((state) => state.getDocumentsForWo(displayWo))
  );
  const documentsCount = useWorkOrderStore((state) =>
    state.getDocumentCountForWo(displayWo)
  );

  const meterReadings = useWorkOrderStore(
    useShallow((state) => state.getMeterReadingsForWo(displayWo))
  );

  const comments = useWorkOrderStore(
    useShallow((state) => state.getCommentsForWo(displayWo))
  );
  const analysisCodes = useWorkOrderStore(
    useShallow((state) => state.getAnalysisCodesForWo(displayWo))
  );
  const downloadedWo = useWorkOrderStore(
    useShallow((state) => state.downloadedWo)
  );

  const actionSheetPermission = useWorkOrderStore((state) =>
    state.getActionPermissionsForWo(displayWo)
  );

  const actionSheetSections = actionSheetPermission ?? DEFAULT_PERMISSIONS;

  const isOffline = useOfflineStore((state) => state.isOffline);
  const hasNetwork = useNetworkStatusStore((state) => state.isOnline);

  const laborCount =
    workOrder?.Labor?.filter((labor: any) => labor.WorkHours !== 0).length ?? 0;

  const resourcesCount = workOrder?.Resources?.length ?? 0;
  const permitsCount = workOrder?.Permits?.length ?? 0;
  const orderDate = workOrder?.Events?.DateCreated;
  const orderTime = workOrder?.Events?.TimeCreated;
  const equipSerial = workOrder?.Equipment;
  const meterCount = meterReadings?.length ?? 0;
  const isLongDescriptionPresent = !!workOrder?.Comment?.LongDescription;
  const isInstructionPresent = !!workOrder?.Instruction;
  let commentsCount = 0;
  if (comments) {
    const commentsArray = comments.split("=====");
    const validComments = commentsArray.filter((comment: any) => {
      return (
        comment.trim().length > 0 &&
        !comment.trim().match(/^\w+, \d{4}-\d{2}-\d{2}/)
      ); // checking for any timestamp format
    });
    commentsCount = validComments.length;
  }

  const history = useHistory();

  const loadWoDetails = useCallback(async () => {
    if (workOrder && !refreshState && !isLoading) return; //we have work order in cache

    const response = await trigger(displayWo);
    if (response.isError) {
      setErrorData(response.data?.toString());
    }
  }, [displayWo, refreshState, trigger, workOrder, isLoading]);

  const loadWoDocuments = useCallback(async () => {
    if (documents && !refreshState && !isLoading) return; //we got the documents from the cache
    if (errorData) return; //error fetching docs
    const response = await getLinkedDocInfoList(displayWo);
    if (response.isError) {
      setErrorData(response.data?.toString());
    } else if (response.data) {
      setWoDocuments(displayWo, response.data);
    }
  }, [
    displayWo,
    documents,
    errorData,
    refreshState,
    setWoDocuments,
    isLoading,
  ]);

  const loadWoMeters = useCallback(async () => {
    if (meterReadings && !refreshState && !isLoading) return; //we have meter reading in the cache

    const response = await getMeterReading(displayWo);
    if (response?.isError) {
      setErrorData(response.data?.toString());
    }
  }, [displayWo, getMeterReading, meterReadings, refreshState, isLoading]);

  const loadData = useCallback(async () => {
    if (isLoading) return; // Safe - prevents duplicate calls

    setIsLoading(true);
    try {
      await loadWoDocuments();
      await loadWoDetails();
      await loadWoMeters();
      await loadCompWizardSections(displayWo);
    } finally {
      setIsLoading(false);
    }
  }, [
    displayWo,
    loadCompWizardSections,
    loadWoDetails,
    loadWoDocuments,
    loadWoMeters,
    isLoading,
  ]);

  const debouncedLoadData = useCallback(
    debounce(loadData, 300), // 300ms delay
    [loadData]
  );

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    if (isLoading) {
      event?.detail.complete();
      return;
    }
    setRefreshState(true);
    await debouncedLoadData();
    event?.detail.complete();
    setRefreshState(false);
  };

  const handleDismiss = useCallback(() => {
    setActionSheetVisible(false);
  }, []);

  useIonViewDidEnter(async () => {
    const shouldLoad =
      !isOffline || !downloadedWo.some((d) => decodeParam(d.id) === displayWo);

    if (!shouldLoad || !displayWo || isLoading) return;

    const fetchData = async () => {
      setShowLoading(true);
      try {
        await loadData();
      } finally {
        setShowLoading(false);
      }
    };

    fetchData();
  }, [isOffline, downloadedWo, displayWo, loadData, isLoading]);

  useEffect(() => {
    (async () => {
      if (isUnmounted.current) return;
      let prevState = history.location.state as any;
      if (
        !!prevState?.prevUrl &&
        prevState?.prevUrl.includes("quick-work-order") &&
        prevState?.prevUrl.includes("create")
      ) {
        setAltBackUrl("/quick-work-order/equipment");
      } else if (!!altBackUrl) {
        setAltBackUrl(undefined);
      }
      setRefreshState(false);
    })();
  }, [
    workOrderId,
    refreshState,
    equipSerial,
    history.location.state,
    altBackUrl,
  ]);

  return (
    <IonPage>
      <PageHeader
        title={displayWo}
        showTimer={true}
        timeAccess={actionSheetSections.TimeEntry}
        showBack={true}
        altBackUrl={altBackUrl}
      >
        <ShareWorkOrderButton
          woNumber={displayWo}
          description={workOrder?.Description}
        />
      </PageHeader>
      <ApiError errorData={errorData} />
      <IonContent fullscreen>
        <WOContent
          handleRefresh={handleRefresh}
          inRefresh={refreshState}
          showLoading={showLoading}
          setShowLoading={setShowLoading}
          workOrder={workOrder}
        />
        <IonList lines="full">
          {/* Add a routerLink prop to these IonItems to route to the proper component */}
          <IonItem
            routerLink={`/workorder/${encodeParam(displayWo)}/details`}
            detail
          >
            {translations["lbl_grp_wo_details"] || "Work Order Details"}
          </IonItem>
          <IonItem
            button
            detail
            lines="full"
            routerLink={`/workorder/${encodeParam(displayWo)}/long-description`}
          >
            <IonLabel>
              {translations["lbl_grp_long_description"] || "Long Description"}
            </IonLabel>
            <IonBadge slot="end">{isLongDescriptionPresent ? 1 : ""}</IonBadge>
          </IonItem>
          <IonItem
            button
            detail
            lines="full"
            routerLink={`/workorder/${encodeParam(displayWo)}/resources`}
          >
            <IonLabel>{translations["lbl_resources"] || "Resources"}</IonLabel>
            <IonBadge slot="end">
              {resourcesCount > 0 ? resourcesCount : ""}
            </IonBadge>
          </IonItem>
          <IonItem
            button
            detail
            lines="full"
            routerLink={`/workorder/${encodeParam(displayWo)}/instructions`}
          >
            <IonLabel>
              {translations["lbl_category_instructions"] || "Instructions"}
            </IonLabel>
            <IonBadge slot="end">{isInstructionPresent ? 1 : ""}</IonBadge>
          </IonItem>
          <IonItem
            button
            detail
            lines="full"
            routerLink={`/workorder/${encodeParam(displayWo)}/permits`}
          >
            <IonLabel>
              {translations["lbl_category_permits"] || "Permits"}
            </IonLabel>
            <IonBadge slot="end">
              {permitsCount > 0 ? permitsCount : ""}
            </IonBadge>
          </IonItem>
          <IonItem
            button
            detail
            lines="full"
            routerLink={`/workorder/${encodeParam(
              displayWo
            )}/remarks/${encodeParam(orderDate)}/${encodeParam(orderTime)}`}
          >
            <IonLabel>
              {translations["lbl_category_comments"] || "Comments"}
            </IonLabel>
            <IonBadge slot="end">
              {commentsCount > 0 ? commentsCount : ""}
            </IonBadge>
          </IonItem>
          <IonItem
            button
            detail
            lines="full"
            routerLink={`/workorder/${encodeParam(displayWo)}/documents`}
          >
            <IonLabel>{translations["lbl_documents"] || "Documents"}</IonLabel>
            <IonBadge slot="end">
              {documentsCount > 0 ? documentsCount : ""}
            </IonBadge>
          </IonItem>
          <IonItem
            button
            detail
            lines="full"
            routerLink={`/workorder/${encodeParam(displayWo)}/labor-activity`}
          >
            <IonLabel>
              {translations["lbl_category_laboractivity"] || "Labor Activity"}
            </IonLabel>
            <IonBadge slot="end">{laborCount > 0 ? laborCount : ""}</IonBadge>
          </IonItem>
          <IonItem
            disabled={meterCount === 0 || !actionSheetSections.Meter}
            button
            detail
            lines="full"
            routerLink={`/workorder/${encodeParam(displayWo)}/meters`}
          >
            <IonLabel>{translations["lbl_category_meter"] || "Meter"}</IonLabel>
            <IonBadge slot="end">
              {meterCount > 0 && actionSheetSections.Meter ? meterCount : ""}
            </IonBadge>
          </IonItem>
          <IonItem
            button
            detail
            disabled={!actionSheetSections.CCA}
            routerLink={`/workorder/${encodeParam(displayWo)}/analysiscodes`}
          >
            <IonLabel>
              {translations["lbl_analysis_codes"] || "Analysis Codes"}
            </IonLabel>
            <IonBadge slot="end">
              {analysisCodes?.length && actionSheetSections.CCA
                ? analysisCodes.length
                : ""}
            </IonBadge>
          </IonItem>
        </IonList>
        <IonFab slot="fixed" vertical="bottom" horizontal="end">
          <IonFabButton
            onClick={() => {
              setActionSheetVisible(true);
            }}
          >
            <IonIcon icon={appsSharp}></IonIcon>
          </IonFabButton>
        </IonFab>

        <WorkOrderActionBar
          isOpen={actionSheetVisible}
          woEquipment={workOrder?.Equipment || ""}
          woEventDateCreated={workOrder?.Events.DateCreated}
          woEventTimeCreated={workOrder?.Events.TimeCreated}
          woNumber={displayWo}
          woDate={orderDate}
          woTime={orderTime}
          inspectionId={workOrder?.InspectionId}
          dismiss={handleDismiss}
          openLoading={() => setShowLoading(true)}
          closeLoading={() => setShowLoading(false)}
          refreshScreen={() => {
            //not in offline or network connect is live
            if (!isOffline || hasNetwork) setRefreshState(true);
          }}
          meterCount={meterCount}
          sectionDisplay={actionSheetSections}
        />
      </IonContent>
    </IonPage>
  );
};

export default withAITracking(
  reactPlugin,
  WorkOrderOverview,
  "WorkOrderOverview"
);
